import { createTheme } from '@mui/material/styles';
import WizardStepIcon from './components/WizardStepIcon';

export function createAppTheme(mode, layout = 'Normal') {
  const isLight = mode === 'light';
  const isCompact = layout === 'Compact';
  const inputSize = isCompact ? 'small' : 'medium';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#1565C0',
        light: '#1E88E5',
        dark: '#0D47A1',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#546E7A',
        light: '#78909C',
        dark: '#37474F',
      },
      background: {
        default: isLight ? '#F5F7FA' : '#121212',
        paper: isLight ? '#FFFFFF' : '#1E1E1E',
      },
      success: {
        main: '#2E7D32',
      },
      warning: {
        main: '#ED6C02',
      },
      error: {
        main: '#D32F2F',
      },
      text: {
        primary: isLight ? '#212121' : '#E0E0E0',
        secondary: isLight ? '#616161' : '#B0B0B0',
      },
      divider: isLight ? '#E0E0E0' : '#333333',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: isCompact ? 13 : 14,
      h4: {
        fontWeight: 600,
        ...(isCompact && { fontSize: '1.5rem' }),
      },
      h5: {
        fontWeight: 600,
        ...(isCompact && { fontSize: '1.25rem' }),
      },
      h6: {
        fontWeight: 600,
        ...(isCompact && { fontSize: '1.1rem' }),
      },
      subtitle1: {
        fontWeight: 500,
      },
      body1: {
        ...(isCompact && { fontSize: '0.875rem' }),
      },
      body2: {
        ...(isCompact && { fontSize: '0.8125rem' }),
      },
    },
    // Compact tightens the global spacing rhythm (every spacing={n}/mb/gap/p is a multiple
    // of this), which is the biggest lever for enterprise-style density.
    spacing: isCompact ? 6 : 8,
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        defaultProps: {
          ...(isCompact && { size: 'small' }),
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: inputSize,
        },
      },
      MuiSelect: {
        defaultProps: {
          size: inputSize,
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: inputSize,
        },
      },
      MuiInputLabel: {
        defaultProps: {
          size: inputSize === 'small' ? 'small' : 'normal',
        },
      },
      MuiTable: {
        defaultProps: {
          ...(isCompact && { size: 'small' }),
        },
      },
      ...(isCompact && {
        // Shorter app bar / sidebar header (content offsets via a Toolbar spacer, so this
        // stays aligned). Override the responsive default at the 600px breakpoint too.
        MuiToolbar: {
          styleOverrides: {
            root: {
              minHeight: 52,
              '@media (min-width:600px)': { minHeight: 52 },
            },
          },
        },
        // Default CardContent padding is 16, and 24 on the last child's bottom — a big silent
        // gap. Tighten both for denser cards.
        MuiCardContent: {
          styleOverrides: {
            root: {
              padding: 12,
              '&:last-child': { paddingBottom: 12 },
            },
          },
        },
      }),
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: isLight
              ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)'
              : '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: isLight ? '#F5F7FA' : '#2A2A2A',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: isLight
              ? '#BDBDBD transparent'
              : '#555555 transparent',
          },
          '*::-webkit-scrollbar': {
            width: 6,
            height: 6,
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            background: isLight ? '#BDBDBD' : '#555555',
            borderRadius: 3,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: isLight ? '#9E9E9E' : '#777777',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${isLight ? '#E0E0E0' : '#333333'}`,
          },
        },
      },
      // Shared wizard step icon (NPA + Validation Protocol wizards). MUI's default
      // StepIcon swaps the number for a CheckCircle once a step is completed — which
      // CSS styleOverrides cannot undo because it's conditional rendering inside the
      // component. Setting a default StepIconComponent here is the theme-level escape
      // hatch: WizardStepIcon keeps the step number at every state (green = completed,
      // blue = active, grey = not yet reached), so every <StepLabel> stays consistent
      // without per-wizard wiring. A wizard may still pass its own StepIconComponent.
      MuiStepLabel: {
        defaultProps: {
          StepIconComponent: WizardStepIcon,
        },
      },
    },
  });
}

// Default export for backward compatibility
const theme = createAppTheme('light');
export default theme;
